import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, BadgeCheck, FileText } from "lucide-react";
import { Rating } from "./Rating";
import { formatINR } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { cn } from "@/lib/utils";

export function ProductCard({ product }) {
  const { addToCart, toggleWishlist, inWishlist, b2bMode } = useStore();
  const wished = inWishlist(product.slug);
  const showB2b = b2bMode && product.b2b_available;

  return (
    <div
      className="group bg-card rounded-xl border border-border overflow-hidden card-hover flex flex-col"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative">
        <Link to={`/product/${product.slug}`} className="block aspect-square overflow-hidden bg-muted">
          <img
            src={product.images?.[0]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {product.vendor_verified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 border border-emerald-100">
              <BadgeCheck size={12} /> Verified
            </span>
          )}
          {product.b2b_available && (
            <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-800 text-[10px] font-semibold px-2 py-0.5 border border-blue-100">
              B2B Bulk
            </span>
          )}
        </div>

        {(product.badges || []).filter((b) => b !== "SALE" && b !== "OUT_OF_STOCK").slice(0, 2).map((b, i) => (
          <span key={b} data-testid={`badge-${b.toLowerCase()}`} style={{ top: `${10 + i * 26}px` }} className="absolute right-2 z-10 rounded-full bg-secondary text-white text-[10px] font-bold tracking-wide px-2 py-0.5">{b}</span>
        ))}
        {product.discount_pct > 0 && (
          <span className="absolute top-2 right-11 rounded-full bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 border border-red-100">
            {product.discount_pct}% OFF
          </span>
        )}

        <button
          onClick={() => toggleWishlist(product)}
          aria-label="Toggle wishlist"
          data-testid={`wishlist-toggle-${product.slug}`}
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 border border-border grid place-items-center hover:bg-white transition-colors"
        >
          <Heart size={16} className={cn(wished ? "fill-secondary text-secondary" : "text-stone-500")} />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <Link
          to={`/vendor/${product.vendor_slug}`}
          className="text-xs text-stone-500 hover:text-secondary flex items-center gap-1 mb-1"
        >
          {product.vendor_name}
          {product.vendor_verified && <BadgeCheck size={11} className="text-emerald-600" />}
        </Link>

        <Link to={`/product/${product.slug}`}>
          <h3 className="font-semibold text-stone-900 text-sm leading-snug line-clamp-2 min-h-[2.5rem] hover:text-secondary transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-1.5">
          <Rating value={product.rating} count={product.reviews_count} />
        </div>

        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold text-secondary">
            {formatINR(showB2b ? product.b2b_price : product.price)}
          </span>
          {!showB2b && product.mrp > product.price && (
            <span className="text-xs text-stone-400 line-through">{formatINR(product.mrp)}</span>
          )}
          {showB2b && <span className="text-[11px] text-stone-500">/ pc</span>}
        </div>

        {showB2b ? (
          <p className="mt-0.5 text-[11px] text-blue-800 font-medium">
            Min. order: {product.moq} units
          </p>
        ) : (
          <p
            className={cn(
              "mt-0.5 text-[11px] font-medium",
              product.in_stock ? "text-emerald-600" : "text-red-500"
            )}
          >
            {product.stock_status}
          </p>
        )}

        <div className="mt-3 pt-3 border-t border-border">
          {showB2b ? (
            <Link
              to="/b2b/request-quote"
              data-testid={`request-quote-${product.slug}`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-secondary text-secondary text-sm font-semibold py-2 hover:bg-secondary hover:text-white transition-colors"
            >
              <FileText size={15} /> Request Quote
            </Link>
          ) : (
            <button
              onClick={() => addToCart(product)}
              disabled={!product.in_stock}
              data-testid={`add-to-cart-${product.slug}`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold py-2 hover:bg-[hsl(28_90%_38%)] transition-colors disabled:opacity-50"
            >
              <ShoppingBag size={15} /> Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
